import { useDisclosure, UseDisclosureReturn } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// Exports
type UseSearchParamsDisclosureArgs = {
  /**
   * The search param key to use. E.g. if `key="comments"`, setting `?comments` will call
   * `disclosure.onOpen()`.
   */
  key: string;

  /**
   * Further filter search param value, for cases where simply checking for param presence is not
   * enough. E.g. `key="comments", filterValue: value => value === "something"` will only open the
   * disclosure if `?comments=something`, and not when `?comments="nope"`.
   */
  filterValue?: (value: string | null) => boolean;

  /** Pass in an existing disclosure. If not provided, a new disclosure is used instead. */
  disclosure?: UseDisclosureReturn;
};

/**
 * Just like Chakra's `useDisclosure`, except it automatically opens the disclosure when:
 * 1. A `?<key>` search param is set, and
 * 2. (Optional) `filterValue` returns `true` for the value of the `?<key>` param
 */
export const useSearchParamsDisclosure = ({
  key,
  filterValue = () => true,
  disclosure: externalDisclosure,
}: UseSearchParamsDisclosureArgs): UseDisclosureReturn => {
  const internalDisclosure = useDisclosure();
  const disclosure = externalDisclosure ?? internalDisclosure;

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    // Can't find search param, nothing more to do
    if (!searchParams.has(key)) return;

    // Run search param through the value filtering function
    if (filterValue(searchParams.get(key))) {
      // Auto-open disclosure when search param is set
      disclosure.onOpen();

      // Clear search param now that the disclosure has been opened
      setSearchParams(
        (params) => {
          params.delete(key);
          return params;
        },
        // We don't want the clearing of our search param to count as a separate browser navigation
        { replace: true },
      );
    }
  }, [disclosure, filterValue, key, searchParams, setSearchParams]);

  return disclosure;
};
